import { Slice, ReplaceAroundStep, belongsTo, Fragment, closest, } from '@tencent/exeditor3-core';
import { GROUP_NAME, LIST_GROUP } from './conf.js';
// 是或类似于 listItem 的节点
export const isListItemLike = (node) => node.type.spec.group === GROUP_NAME;
export function closestListItem($pos) {
    return closest($pos, (node) => isListItemLike(node)).node;
}
export function closestList($pos) {
    return closest($pos, isListNode).node;
}
export function isListNode(node) {
    return belongsTo(node, LIST_GROUP);
}
// 把 pos 前后紧毗邻的同类 list join 到一起(</ol>|<ol>)
export const joinSameList = (tr) => {
    const toJoinPoses = [];
    tr.selection.ranges.forEach((range) => {
        const from = range.$from.pos;
        const to = range.$to.pos;
        tr.doc.nodesBetween(from, to, (node, pos, parent, index) => {
            if (!parent) {
                return false;
            }
            if (!isListNode(node)) {
                return !node.isTextblock;
            }
            if (index > 0 && node.type === parent.child(index - 1).type) {
                toJoinPoses.push(pos);
            }
            if (index < parent.childCount - 1 &&
                node.type === parent.child(index + 1).type) {
                toJoinPoses.push(pos + node.nodeSize);
            }
            return true;
        });
    });
    // 去重然后倒排
    toJoinPoses
        .filter((p, index) => toJoinPoses.indexOf(p) === index)
        .sort((a, b) => b - a)
        .forEach((pos) => {
        tr.delete(pos - 1, pos + 1);
    });
};
// 把选区内的同种 list 合并，统一编排序号
export const joinSameListInSelection = (tr) => {
    const toJoinPoses = [];
    tr.selection.ranges.forEach((range) => {
        const from = range.$from.pos;
        const to = range.$to.pos;
        tr.doc.nodesBetween(from, to, (node, pos, parent, index) => {
            var _a, _b, _c, _d;
            if (pos < from && pos + node.nodeSize > to) {
                return true;
            }
            if (!parent) {
                return false;
            }
            if (!isListNode(node)) {
                return !node.isTextblock;
            }
            if (index > 0 &&
                node.type === parent.child(index - 1).type &&
                ((_a = node.attrs) === null || _a === void 0 ? void 0 : _a.listStyleType) ===
                    ((_b = parent.child(index - 1).attrs) === null || _b === void 0 ? void 0 : _b.listStyleType) &&
                pos > from) {
                toJoinPoses.push(pos);
            }
            if (index < parent.childCount - 1 &&
                node.type === parent.child(index + 1).type &&
                ((_c = node.attrs) === null || _c === void 0 ? void 0 : _c.listStyleType) ===
                    ((_d = parent.child(index + 1).attrs) === null || _d === void 0 ? void 0 : _d.listStyleType) &&
                pos + node.nodeSize < to) {
                toJoinPoses.push(pos + node.nodeSize);
            }
            return true;
        });
    });
    // 去重然后倒排
    toJoinPoses
        .filter((p, index) => toJoinPoses.indexOf(p) === index)
        .sort((a, b) => b - a)
        .forEach((pos) => {
        tr.delete(pos - 1, pos + 1);
    });
};
// 重写 prosemirror-schema-list 中的 sinkListItem 函数。
export const sinkListItem = (itemType) => (state, dispatch) => {
    const { $from, $to } = state.selection;
    const range = $from.blockRange($to, (node) => node.childCount > 0 && node.firstChild.type === itemType);
    if (!range) {
        return false;
    }
    const { startIndex } = range;
    if (startIndex === 0) {
        return false;
    }
    const { parent } = range;
    const nodeBefore = parent.child(startIndex - 1);
    if (nodeBefore.type !== itemType) {
        return false;
    }
    if (dispatch) {
        const nestedBefore = nodeBefore.lastChild && nodeBefore.lastChild.type === parent.type;
        const inner = Fragment.from(nestedBefore ? itemType.create() : null);
        const { start, ...rest } = parent.attrs;
        // 与官方不同的地方主要是这里，创建子 ol 的时候继承父 ol 的属性
        const slice = new Slice(Fragment.from(itemType.create(null, Fragment.from(parent.type.create({ ...rest }, inner)))), nestedBefore ? 3 : 1, 0);
        const before = range.start;
        const after = range.end;
        dispatch(state.tr
            .step(new ReplaceAroundStep(before - (nestedBefore ? 3 : 1), after, before, after, slice, 1, true))
            .scrollIntoView());
    }
    return true;
};
// 获取选区内所有的不重复的 listItem 、纯文本块等节点类型与位置信息
export const getNodeInfosInSelection = (state, nodeType, itemNodeType) => {
    const { tr } = state;
    // 采集关键位置(|<li> 或对于不在 list 中的 |<textBlock>)
    let nodesInfo = [];
    tr.selection.ranges.forEach((range) => {
        const from = range.$from.pos;
        const to = range.$to.pos;
        // nodesBetween 会从上到下遍历选区所在树的所有节点，中间非选区内的节点都不是 text 节点，只有选区内的文本才为 text 节点
        tr.doc.nodesBetween(from, to, (node, pos) => {
            if (!node.isTextblock) {
                return true;
            }
            const $pos = tr.doc.resolve(pos);
            // 当前位置最近的 listItem 节点及起始位置
            const { node: listItem, pos: oldPos } = closest($pos, (node) => isListItemLike(node));
            if (listItem) {
                // 寻找 listItem 最近的 List，也就是它的 parentNode
                const parent = closestList($pos);
                let type = null;
                if (parent.type === nodeType) {
                    type = 0 /* POIType.SameListSameItem */;
                }
                else if (listItem.type === itemNodeType) {
                    type = 1 /* POIType.OtherListSameItem */;
                }
                else {
                    type = 2 /* POIType.OtherListOtherItem */;
                }
                nodesInfo.push({ type, oldPos, size: listItem.nodeSize });
            }
            else {
                nodesInfo.push({ type: 3 /* POIType.BareTextBlock */, oldPos: pos });
            }
            return false;
        });
    });
    // 将位置记录去重并从小到大排序
    nodesInfo = nodesInfo
        .filter(({ oldPos }, index) => nodesInfo.findIndex((v) => v.oldPos === oldPos) === index)
        .sort((a, b) => a.oldPos - b.oldPos);
    return nodesInfo;
};
// 给定的所有数组元素，是否为同一种列表（有序或无序）
export const getIsSameListSameItem = (listItemsInfo) => listItemsInfo.filter(({ type }) => type !== 0 /* POIType.SameListSameItem */)
    .length === 0;
//# sourceMappingURL=listMethods.js.map